import { Button, Spin, Tabs, type TabsProps, Tag } from "antd";
import {
  BarChart3,
  Crown,
  ExternalLink,
  FileText,
  MapPin,
  Phone,
  Users,
  Wallet,
} from "lucide-react";
import { useEffect, useState } from "react";

import Modal from "@/components/Modal";
import { OssPreviewImage } from "@/components/OssPreviewImage";

import { getMemberStatusTagColor } from "../../constants";
import { AccountTab } from "./AccountTab";
import styles from "./MemberDetailModal.module.css";
import { ProfileTab } from "./ProfileTab";
import { StatsTab } from "./StatsTab";
import { useMemberDetail } from "./useMemberDetail";

/** 会员详情 Modal props */
export interface MemberDetailModalProps {
  open: boolean;
  /** 会员 ID */
  memberId: string;
  onClose: () => void;
  /** 打开所属门店详情 */
  onOpenStore: (shopId?: string) => void;
  /** 打开关联账号详情 */
  onOpenAccount?: (userId?: string) => void;
}

/**
 * 会员详情 Modal：头图 + 账户 / 档案 / 统计
 */
export const MemberDetailModal = ({
  open,
  memberId,
  onClose,
  onOpenStore,
  onOpenAccount,
}: MemberDetailModalProps) => {
  // 当前激活的主 Tab
  const [activeTab, setActiveTab] = useState("account");
  const {
    detail,
    loading,
    remarkSaving,
    tagSaving,
    handleAccountLogPageChange,
    saveRemark,
    addTag,
    removeTag,
  } = useMemberDetail(memberId, open);

  // 关闭时重置 Tab
  useEffect(() => {
    if (open) return;
    setActiveTab("account");
  }, [open]);

  const tabItems: TabsProps["items"] = detail
    ? [
        {
          key: "account",
          label: (
            <span className="inline-flex items-center gap-1.5">
              <Wallet className="w-3.5 h-3.5" />
              账户
            </span>
          ),
          children: (
            <AccountTab
              balance={detail.accountBalance}
              growthValueRequired={detail.growthValueRequired}
              accountLogs={detail.accountLogs}
              onLogPageChange={handleAccountLogPageChange}
            />
          ),
        },
        {
          key: "profile",
          label: (
            <span className="inline-flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5" />
              档案
            </span>
          ),
          children: (
            <ProfileTab
              detail={detail}
              remarkSaving={remarkSaving}
              tagSaving={tagSaving}
              onSaveRemark={saveRemark}
              onAddTag={addTag}
              onRemoveTag={removeTag}
            />
          ),
        },
        {
          key: "stats",
          label: (
            <span className="inline-flex items-center gap-1.5">
              <BarChart3 className="w-3.5 h-3.5" />
              统计
            </span>
          ),
          children: (
            <StatsTab
              memberId={detail.memberId}
              shopId={detail.shopId}
              stats={detail.consumptionStats}
            />
          ),
        },
      ]
    : [];

  return (
    <Modal
      open={open}
      width={900}
      title="会员详情"
      titleIcon={<Users className="w-5 h-5 text-orange-500" />}
      // 定高：min 对齐图二，height 封顶 80vh，内部账户明细才能滚
      styles={{ content: { minHeight: 640, height: "80vh", maxHeight: "80vh" } }}
      contentClassName="p-0! bg-[#f5f5f5] flex flex-col overflow-hidden!"
      footer={
        <div className="flex justify-end">
          <Button onClick={onClose}>关闭</Button>
        </div>
      }
      onCancel={onClose}
    >
      <Spin spinning={loading} wrapperClassName={`h-full ${styles.spinFill}`}>
        {!detail && !loading ? (
          <div className="py-16 text-center text-sm text-slate-400">暂无会员详情</div>
        ) : detail ? (
          <div className="flex flex-col h-full min-h-0 px-5 pt-4 pb-2">
            {/* 会员头图名片 */}
            <div className="flex items-center gap-4 p-3 mb-1 bg-white/80 rounded-xl border border-slate-200/70 shrink-0">
              <div className="w-14 h-14 rounded-xl bg-linear-to-br from-orange-400 via-orange-500 to-amber-500 flex items-center justify-center shrink-0 overflow-hidden shadow-sm">
                <OssPreviewImage
                  fileUrl={detail.avatarUrl}
                  alt={detail.name || "会员头像"}
                  className="w-full h-full object-cover"
                  fallback={<Crown className="h-6 w-6 text-white" />}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h3 className="font-bold text-lg text-slate-900 m-0 leading-none">
                    {detail.name || "-"}
                  </h3>
                  {detail.levelName ? (
                    <Tag bordered={false} className="m-0 text-xs text-slate-600 bg-slate-100">
                      {detail.levelName}
                    </Tag>
                  ) : null}
                  <Tag
                    bordered={false}
                    color={getMemberStatusTagColor(detail.memberStatus)}
                    className="m-0 text-xs"
                  >
                    {detail.memberStatusLabel || detail.memberStatus || "-"}
                  </Tag>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                  <span className="inline-flex items-center gap-1">
                    <Phone className="h-3 w-3 shrink-0" />
                    手机 {detail.phone || "-"}
                  </span>
                  {detail.userId && onOpenAccount ? (
                    <>
                      <span className="w-px h-3 bg-slate-200 shrink-0" />
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-slate-700 hover:text-orange-600 hover:underline"
                        onClick={() => onOpenAccount(detail.userId)}
                      >
                        关联账号
                        <ExternalLink className="h-2.5 w-2.5 shrink-0" />
                      </button>
                    </>
                  ) : null}
                  {detail.shopName ? (
                    <>
                      <span className="w-px h-3 bg-slate-200 shrink-0" />
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-slate-700 hover:text-orange-600 hover:underline"
                        onClick={() => onOpenStore(detail.shopId)}
                      >
                        <MapPin className="h-3 w-3 shrink-0" />
                        <span className="truncate max-w-70">{detail.shopName}</span>
                        <ExternalLink className="h-2.5 w-2.5 shrink-0" />
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
            <Tabs
              activeKey={activeTab}
              items={tabItems}
              className={styles.detailTabs}
              onChange={setActiveTab}
            />
          </div>
        ) : (
          <div className="py-16" />
        )}
      </Spin>
    </Modal>
  );
};

export default MemberDetailModal;
